
// Packages
import { defineComponent, PropType } from 'vue';
import { isBookingPortalFlow } from '@white-label-helper/booking-portal-flow';
import { isLoungePortal } from '@white-label-helper/shared-helpers';

// Helpers
import {
  ACCOUNT_URLS,
  AccountURL,
  DOMAINS_NAMES,
  ECOMMERCE_URLS,
  getDomainUrl,
  goToApp
} from '@white-label-helper/switch-app';
import { getAppHeroProduct } from '@white-label-helper/get-app-hero-product';
import { getAppVariable } from '@white-label-helper/get-app-variable';
// Types
import type { Partners } from '@white-label-types/partners-api';
import type { DesktopMenuData } from '../user-menu-desktop/user-menu-desktop.vue';
import UserMenuDesktop from '../user-menu-desktop/user-menu-desktop.vue';
import type { MobileMenuData, TabData } from '../user-menu-mobile/user-menu-mobile.vue';
import UserMenuMobile from '../user-menu-mobile/user-menu-mobile.vue';
import type { Locale } from '@white-label-types/internationalization';
import {
  COLOUR_SCHEMES,
  ROUTE_NAMES,
  APP_HERO_PRODUCTS,
  HeroProduct,
  FEATURE_FLAGS,
 } from '@white-label-configuration/constants';
import { setupLinkProtocol } from '@white-label-helper/setup-link-protocol';
// Components
import { BrandLogo, CavuButton } from 'ui-shared-components';
import NavbarTabs from '../navbar-tabs/navbar-tabs.vue';
import PartnerLink from '../navbar-partner-link/navbar-partner-link.vue';
import LanguageSelectorButton from '../language-selector-button/language-selector-button.vue';
import NavbarBasket from '../navbar-basket/navbar-basket.vue';

export type MenuDataOrUndefined = DesktopMenuData | MobileMenuData | undefined;

export type TabDataOrNull = TabData | null;

type ColourScheme = (typeof COLOUR_SCHEMES)[keyof typeof COLOUR_SCHEMES];

export default defineComponent({
  name: 'Navbar',

  components: {
    BrandLogo,
    CavuButton,
    LanguageSelectorButton,
    NavbarTabs,
    PartnerLink,
    UserMenuDesktop,
    UserMenuMobile,
    NavbarBasket,
  },

  props: {
    logoRedirectURL: {
      required: true,
      type: String as PropType<Partners['logo']>,
    },

    logoURL: {
      required: true,
      type: String as PropType<Partners['logo']>,
    },

    partnerName: {
      required: true,
      type: String as PropType<Partners['partner_name']>,
    },

    showLoginFlow: {
      default: true,
      type: Boolean,
    },

    showFooter: {
      default: true,
      type: Boolean,
    },

    tabsData: {
      default: () => null,
      type: Object as PropType<TabDataOrNull>,
      required: false,
    },
  },

  computed: {
    /** The URL to send a customer when clicking on the logo */
    homePageUrl(): string | null {
      const discountFromQuery = this.$route.query['discount'];

      let redirectURL = this.$props.logoRedirectURL;

      if (discountFromQuery && typeof discountFromQuery === 'string') {
        redirectURL += `/?discount=${discountFromQuery}`;
      }

      return setupLinkProtocol(redirectURL);
    },

    /**
     * Build the alt text for the brand logo out of the partner name
     */
    altText(): VueI18n.LocaleMessage {
      return this.$t('layouts.logo.alt', { partnerName: this.partnerName })
    },

    /**
     * Checks if login is enabled or not on Launch Darkly.
     */
    loginEnabled(): boolean {
      if (!process.client || !this.$launchDarkly) return false;
      return (
        this.$launchDarkly.variation(
          FEATURE_FLAGS.WL_FEATURE_CONTROL_PER_PARTNER_ENABLE_MY_ACCOUNT
        ) === true
      );
    },

    /**
     * Returned of the user is logged in or not
     */
    isUserLoggedIn(): boolean {
      //@ts-ignore - cannot find type
      return this.$auth.isAuthenticated;
    },

    /**
     * Login button text is based on if the feature toggle is in, and if the customer is login in or not
     */
    loginButtonText(): VueI18n.TranslateResult {
      return this.loginEnabled && !this.isMigrationSupportInProgress
        ? this.$t('shared.buttons.login')
        : this.$t('UI.tabs.myBookings');
    },

    /**
     * What URL to pass in based on if logging in is enabled and if the user is login or not
     */
    accountUrl(): AccountURL {
      if (!this.loginEnabled) {
        return ACCOUNT_URLS.SEARCH_BOOKING;
      }

      return this.isUserLoggedIn ? ACCOUNT_URLS.HOME : ACCOUNT_URLS.LOGIN;
    },

    showMyBookingsButton(): boolean {
      return this.showLoginFlow ?? !this.isUserLoggedIn;
    },

    menuData(): MenuDataOrUndefined {
      return this.isUserLoggedIn
        ? {
            nickname: this.$auth.user?.['name'] as string,
          logoutHandler: () => this.$auth.logout(),
            makeNewBookingHandler: () =>
              goToApp(
                getDomainUrl(DOMAINS_NAMES.ECOMMERCE),
                ECOMMERCE_URLS.HOME
              ),
          }
        : undefined;
    },

    colourScheme(): ColourScheme {
      const scheme = getAppVariable('colours.header.font');
      if (scheme === COLOUR_SCHEMES.LIGHT || scheme === COLOUR_SCHEMES.DARK) {
        return scheme;
      }
      return COLOUR_SCHEMES.DARK;
    },

    tabsLinks(): TabData['tabsLinks'] {
      return this.tabsData?.tabsLinks || {};
    },
    getRoutePath(): string {
      return this.$route.path || '';
    },
    availableLanguages() {
      const partnerLanguages = getAppVariable('languages') as Locale[];
      if (!partnerLanguages) return [];
      //@ts-ignore `locales` is part of nuxt-i18n, not recognised by typescript
      return this.$i18n.locales.filter((locale: Locale) =>
        partnerLanguages.map((language) => language.code).includes(locale.code)
      );
    },
    heroProduct(): HeroProduct {
      return getAppHeroProduct(APP_HERO_PRODUCTS.PARKING);
    },
    showLanguageSelector(): boolean {
      const isLounges = this.heroProduct === APP_HERO_PRODUCTS.LOUNGES;
      const isParking = this.heroProduct === APP_HERO_PRODUCTS.PARKING;
      const isTravelInsurance = this.heroProduct === APP_HERO_PRODUCTS.TRAVEL_INSURANCE;
      const url = window?.location?.href;

      return (
        (isParking || isLounges || isTravelInsurance) &&
        //@ts-ignore `locales` is part of nuxt-i18n, not recognised by typescript
        this.$i18n.locales.length > 1 &&
        url.includes(getDomainUrl(DOMAINS_NAMES.ECOMMERCE)) &&
        (this.$route.name === ROUTE_NAMES.home || this.$route.name === ROUTE_NAMES.search)
      );
    },

    isMigrationSupportInProgress(): boolean {
      const migrationSupport = getAppVariable('migration_support');
      return migrationSupport?.is_in_progress;
    },
    isBookingPortalFlowEnabled(): boolean {
      return isBookingPortalFlow();
    },
    showSkipToMainContentLink(): boolean {
      return this.$route.name === ROUTE_NAMES.search;
    },
  },

  methods: {
    isLoungePortal,
    goToMyBookings(): void {
      //@ts-ignore - cannot find types
      if (this.isMigrationSupportInProgress) {
        const migrationSupport = getAppVariable('migration_support');

        if (migrationSupport?.is_in_progress) {
          window.location.href = migrationSupport.manage_booking_url;
          return;
        }
      }

      //@ts-ignore - cannot find types
      if (!this.isUserLoggedIn && window.location.pathname.includes(ACCOUNT_URLS.CALLBACK)) {
        // redirects to auth0 login page only on callback page
        this.$auth.logout();
      }
      else {
        // finally, redirect to account app
        goToApp(
          getDomainUrl(DOMAINS_NAMES.ACCOUNT),
          //@ts-ignore - cannot find types
          this.accountUrl
        );
      }
    },
  },
});
